import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Users, Plus, X, Loader2, UserPlus } from 'lucide-react';
import { groupsAPI } from '@/services/api';
import { toast } from 'sonner';

const CreateGroupPage = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [memberPhones, setMemberPhones] = useState([]);
  const [currentPhone, setCurrentPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isValidName = name.trim().length >= 2;
  const isValidPhone = currentPhone.length === 10;
  const canSubmit = isValidName;

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setCurrentPhone(value);
    setError('');
  };

  const addMember = () => {
    if (!isValidPhone) return;
    
    if (memberPhones.includes(currentPhone)) {
      setError('This number is already added');
      return;
    }
    
    setMemberPhones([...memberPhones, currentPhone]);
    setCurrentPhone('');
    setError('');
  };

  const removeMember = (phone) => {
    setMemberPhones(memberPhones.filter(p => p !== phone));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setLoading(true);
    setError('');

    try {
      const response = await groupsAPI.create({
        name: name.trim(),
        member_phones: memberPhones,
      });
      
      toast.success('Group created!');
      navigate(`/groups/${response.data.id}`);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to create group';
      setError(typeof message === 'string' ? message : 'Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="sticky top-0 bg-white z-10 px-6 py-4 flex items-center gap-4 border-b-2 border-black">
        <button 
          onClick={() => navigate('/groups')}
          className="hover:opacity-70 transition-opacity"
          data-testid="back-button"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-xl font-heading font-bold">Create Group</h1>
      </header>

      <main className="flex-1 p-6 pb-32 space-y-6">
        {/* Group Icon Preview */}
        <div className="text-center">
          <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-hard border-2 border-black">
            <Users className="w-12 h-12 text-white" />
          </div>
        </div>

        {/* Group Name */}
        <div>
          <label className="text-sm font-medium text-neutral-500 uppercase tracking-wide block mb-2">
            Group Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError('');
            }}
            placeholder="e.g., Goa Trip 2024, Roommates, Office Lunch"
            className="input-base"
            autoFocus
            data-testid="group-name-input"
          />
        </div>

        {/* Add Members */}
        <div>
          <label className="text-sm font-medium text-neutral-500 uppercase tracking-wide block mb-2">
            Add Members (Optional)
          </label>
          <p className="text-xs text-neutral-400 mb-3">
            Add friends by their phone number. They must be on SplitPe.
          </p>
          
          <div className="flex gap-2 mb-4">
            <div className="flex items-center justify-center w-16 h-14 rounded-xl border-2 border-black bg-neutral-100 font-bold text-sm">
              +91
            </div>
            <input
              type="tel"
              value={currentPhone}
              onChange={handlePhoneChange}
              placeholder="Phone number"
              className="input-base flex-1"
              data-testid="member-phone-input"
              onKeyPress={(e) => e.key === 'Enter' && addMember()}
            />
            <button
              onClick={addMember}
              disabled={!isValidPhone}
              className="w-14 h-14 rounded-xl border-2 border-black bg-emerald-500 text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-emerald-600 transition-colors"
              data-testid="add-member-button"
            >
              <UserPlus className="w-5 h-5" />
            </button>
          </div>

          {/* Member List */}
          {memberPhones.length > 0 && (
            <div className="space-y-2">
              {memberPhones.map((phone, index) => (
                <div 
                  key={phone}
                  className="flex items-center justify-between p-3 rounded-xl border-2 border-black bg-gradient-to-r from-pink-50 to-violet-50"
                  data-testid={`member-item-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-violet-500 flex items-center justify-center text-white font-bold text-sm">
                      {phone[0]}
                    </div>
                    <span className="font-medium">+91 {phone}</span>
                  </div>
                  <button
                    onClick={() => removeMember(phone)}
                    className="w-8 h-8 rounded-full hover:bg-red-100 flex items-center justify-center text-red-500 transition-colors"
                    data-testid={`remove-member-${index}`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {memberPhones.length === 0 && (
            <div className="text-center py-6 border-2 border-dashed border-neutral-300 rounded-xl">
              <UserPlus className="w-8 h-8 mx-auto text-neutral-300 mb-2" />
              <p className="text-sm text-neutral-400">
                You can add members now or later
              </p>
            </div>
          )}
        </div>

        {error && (
          <p className="text-error text-sm font-medium text-center" data-testid="error-message">
            {error}
          </p>
        )}

        {/* Tips Card */}
        <div className="card-base bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300">
          <h3 className="font-bold text-amber-800 mb-2">Pro Tip</h3>
          <p className="text-sm text-amber-700">
            Groups are perfect for trips, roommates, events, or any recurring expense sharing!
          </p>
        </div>
      </main>

      {/* Fixed Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-6 bg-white border-t-2 border-black">
        <button
          onClick={handleSubmit}
          disabled={!canSubmit || loading}
          className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="create-group-submit"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              Create Group
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default CreateGroupPage;
